// src/bind-this.ts
var hasOwnProperty = Object.hasOwnProperty;
var bindThisHandler = {
  get: (target, property) => {
    if (hasOwnProperty.call(target, property)) {
      return target[property];
    }
    const m = target[property];
    if (typeof m === "function") {
      return target[property] = m.bind(target);
    }
    return m;
  }
};
var bindThisProxyMap = /* @__PURE__ */ new WeakMap();
function bindThis(obj) {
  let proxy = bindThisProxyMap.get(obj);
  if (!proxy) {
    proxy = new Proxy(obj, bindThisHandler);
    bindThisProxyMap.set(obj, proxy);
  }
  return proxy;
}

// src/prop-hook.ts
var initPropHookSymbol = Symbol.for("Serenity.initPropHook");

// src/util.ts
function isElement(val) {
  return !!(val && typeof val.nodeType === "number");
}
function isString(val) {
  return typeof val === "string";
}
function isNumber(val) {
  return typeof val === "number";
}
function isObject(val) {
  return typeof val === "object" && val !== null;
}
function isComponentClass(val) {
  return !!(val && val.isComponent);
}
function isArrayLike(val) {
  return isObject(val) && typeof val.length === "number" && typeof val.nodeType !== "number";
}
function isPropHook(value) {
  return typeof value === "function" && typeof value[initPropHookSymbol] === "function";
}
function isVisibleChild(val) {
  return val != null && typeof val !== "boolean";
}

// src/class-name.ts
function className(value) {
  if (Array.isArray(value)) {
    return value.map(className).filter(Boolean).join(" ");
  } else if (isObject(value)) {
    if (Symbol.iterator in value) {
      return className(Array.from(value));
    }
    return Object.keys(value).filter((k) => value[k]).join(" ");
  } else if (isVisibleChild(value)) {
    return "" + value;
  } else {
    return "";
  }
}

// src/ref.ts
function createRef() {
  return Object.seal({ current: null });
}
function isRefObject(maybeRef) {
  return maybeRef != null && typeof maybeRef === "object" && "current" in maybeRef;
}
function setRef(ref, current) {
  if (isRefObject(ref)) {
    ref.current = current;
  } else if (typeof ref === "function") {
    ref(current);
  }
}

// src/component.ts
var Component = class {
  constructor(props) {
    this.props = props;
  }
  render() {
    return null;
  }
};
Component.isComponent = true;
function initComponentClass(Class, attr, children) {
  attr = { ...attr, children };
  const instance = new Class(attr);
  const node = instance.render();
  if ("ref" in attr) {
    setRef(attr.ref, instance);
  }
  return node;
}

// src/mathml-consts.ts
var MathMLNamespace = "http://www.w3.org/1998/Math/MathML";
var mathMLOnlyTags = /* @__PURE__ */ new Set(["math", "annotation", "annotation-xml", "maction", "merror", "mfrac", "mi", "mmultiscripts", "mn", "mo", "mover", "mpadded", "mphantom", "mprescripts", "mroot", "mrow", "ms", "mspace", "msqrt", "mstyle", "msub", "msubsup", "msup", "mtable", "mtd", "mtext", "mtr", "munder", "munderover", "semantics", "menclose", "mfenced"]);

// src/svg-consts.ts
var svgOnlyTags = /* @__PURE__ */ new Set([
  // "a",
  "animate",
  "animateMotion",
  "animateTransform",
  "circle",
  "clipPath",
  "defs",
  "desc",
  "ellipse",
  "feBlend",
  "feColorMatrix",
  "feComponentTransfer",
  "feComposite",
  "feConvolveMatrix",
  "feDiffuseLighting",
  "feDisplacementMap",
  "feDistantLight",
  "feDropShadow",
  "feFlood",
  "feFuncA",
  "feFuncB",
  "feFuncG",
  "feFuncR",
  "feGaussianBlur",
  "feImage",
  "feMerge",
  "feMergeNode",
  "feMorphology",
  "feOffset",
  "fePointLight",
  "feSpecularLighting",
  "feSpotLight",
  "feTile",
  "feTurbulence",
  "filter",
  "foreignObject",
  "g",
  "image",
  "line",
  "linearGradient",
  "marker",
  "mask",
  "metadata",
  "mpath",
  "path",
  "pattern",
  "polygon",
  "polyline",
  "radialGradient",
  "rect",
  // "script",
  "set",
  "stop",
  // "style",
  "svg",
  "switch",
  "symbol",
  "text",
  "textPath",
  // "title",
  "tref",
  "tspan",
  "use",
  "view"
]);
var nonPresentationSVGAttributes = /^(a(ll|t|u)|base[FP]|c(al|lipPathU|on)|di|ed|ex|filter[RU]|g(lyphR|r)|ke|l(en|im)|ma(rker[HUW]|s)|n|pat|pr|point[^e]|re[^n]|s[puy]|st[^or]|ta|textL|vi|xC|y|z)/;
var SVGNamespace = "http://www.w3.org/2000/svg";

// src/in-namespace-uri.ts
var jsxNamespaceURISymbol = Symbol.for("Serenity.jsxNamespaceURI");
function currentNamespaceURI(value) {
  const current = globalThis[jsxNamespaceURISymbol];
  if (!arguments.length) {
    return current;
  }
  globalThis[jsxNamespaceURISymbol] = value;
  return current;
}
function inNamespaceURI(namespaceURI, children) {
  const prev = currentNamespaceURI(namespaceURI);
  let result;
  try {
    result = children();
  } finally {
    currentNamespaceURI(prev);
  }
  return result;
}
function inSVGNamespace(fn) {
  return inNamespaceURI(SVGNamespace, fn);
}
function inMathMLNamespace(fn) {
  return inNamespaceURI(MathMLNamespace, fn);
}
function inHTMLNamespace(fn) {
  return inNamespaceURI(null, fn);
}

// src/shadow.ts
var jsxTypeKeySymbol = Symbol.for("Serenity.jsxTypeKey");
var shadowRootKey = "ShadowRoot";
function ShadowRootNode({
  children,
  ref,
  ...attr
}) {
  return {
    [jsxTypeKeySymbol]: shadowRootKey,
    ref,
    attr,
    children
  };
}
function isShadowRoot(el) {
  return el != null && el[jsxTypeKeySymbol] === shadowRootKey;
}

// src/disposing-listener.ts
var disposingListenersSymbol = Symbol.for("Serenity.disposingListeners");
var lifecycleRootSymbol = Symbol.for("Serenity.lifecycleRoot");
function getDisposingListeners() {
  return globalThis[disposingListenersSymbol] || (globalThis[disposingListenersSymbol] = /* @__PURE__ */ new WeakMap());
}
function disposingEventListener(ev) {
  if (ev && ev.target && (!ev.currentTarget || ev.currentTarget === ev.target))
    invokeDisposingListeners(ev?.target);
}
function dispatchDisposingEvent(target, opt) {
  if (!target || typeof CustomEvent !== "function")
    return;
  const event = new CustomEvent("disposing", {
    bubbles: opt?.bubbles ?? false,
    cancelable: opt?.cancelable ?? false
  });
  target.dispatchEvent(event);
}
function invokeDisposingListeners(node, opt) {
  if (!node)
    return;
  const disposingListeners = getDisposingListeners();
  function invokeFor(el) {
    const listeners = disposingListeners.get(el);
    if (!listeners)
      return;
    disposingListeners.delete(el);
    el.removeEventListener("disposing", disposingEventListener);
    for (const disposer of listeners) {
      try {
        disposer.callback(el);
      } catch {
      }
    }
  }
  if (opt?.descendants && node instanceof Element && node.hasChildNodes()) {
    const descendants = [];
    const iterator = document.createNodeIterator(
      node,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT | NodeFilter.SHOW_COMMENT
    );
    let currentNode;
    while (currentNode = iterator.nextNode()) {
      if (currentNode !== node) {
        descendants.push(currentNode);
      }
    }
    for (let i2 = 0; i2 < descendants.length; i2++) {
      invokeFor(descendants[i2]);
    }
  }
  if (!opt?.excludeSelf) {
    invokeFor(node);
  }
}
function addDisposingListener(target, handler, regKey) {
  if (!target || !handler)
    return target;
  const disposingListeners = getDisposingListeners();
  let listeners = disposingListeners.get(target);
  if (!listeners) {
    if (typeof target.addEventListener !== "function")
      return target;
    disposingListeners.set(target, listeners = [{
      callback: handler,
      regKey
    }]);
    target.addEventListener("disposing", disposingEventListener, { once: true });
    return target;
  }
  const existing = listeners.find((x) => x.callback === handler);
  if (existing) {
    if (existing.regKey && regKey && existing.regKey !== regKey) {
      throw new Error("A disposing listener with the same callback but different regKey is already registered on the target element.");
    }
    if (regKey && !existing.regKey) {
      existing.regKey = regKey;
    }
    return target;
  }
  listeners.push({ callback: handler, regKey });
  return target;
}
function removeDisposingListener(target, handler, regKey) {
  if (!target || !handler && !regKey)
    return target;
  const disposingListeners = getDisposingListeners();
  const listeners = disposingListeners.get(target);
  if (listeners) {
    for (let index = listeners.length - 1; index >= 0; index--) {
      const listener = listeners[index];
      if (regKey && listener.regKey === regKey || handler && handler === listener.callback) {
        listeners.splice(index, 1);
      }
    }
    if (listeners.length === 0) {
      disposingListeners.delete(target);
      if (typeof target.removeEventListener === "function") {
        target.removeEventListener("disposing", disposingEventListener);
      }
    }
  }
  return target;
}
function currentLifecycleRoot(...args) {
  if (args.length > 0) {
    const prev = globalThis[lifecycleRootSymbol] || null;
    globalThis[lifecycleRootSymbol] = args[0] || null;
    return prev;
  }
  return globalThis[lifecycleRootSymbol] || null;
}

// src/signal-util.ts
function isSignalLike(obj) {
  return obj != null && typeof obj === "object" && typeof obj.subscribe === "function" && typeof obj.peek === "function" && "value" in obj;
}
var SignalObserveArgsImpl = class {
  constructor(signal2, lifecycleRoot, lifecycleNode) {
    this.signal = signal2;
    this.isInitial = true;
    this.hasChanged = false;
    this.lifecycleRoot = lifecycleRoot;
    this._node = lifecycleNode;
  }
  get lifecycleNode() {
    return this._node;
  }
  delDispose() {
    removeDisposingListener(this._node, this.effectDisposer);
    removeDisposingListener(this._node, this.signal?.derivedDisposer);
  }
  addDispose() {
    addDisposingListener(this._node, this.effectDisposer);
    addDisposingListener(this._node, this.signal?.derivedDisposer);
  }
  get effectDisposer() {
    return this._dispose;
  }
  set effectDisposer(value) {
    this.delDispose();
    this._dispose = value;
    this.addDispose();
  }
  set lifecycleNode(value) {
    if (value !== this._node) {
      this.delDispose();
      this._node = value;
      this.addDispose();
    }
  }
};
function observeSignal(signal2, callback, opt) {
  const lifecycleRoot = opt?.useLifecycleRoot ? currentLifecycleRoot() : void 0;
  const args = new SignalObserveArgsImpl(signal2, lifecycleRoot, opt?.lifecycleNode);
  const disposer = args.signal.subscribe(function(value) {
    args.newValue = value;
    if (args.isInitial && this?.dispose) {
      args.effectDisposer = this.dispose.bind(this);
    }
    args.hasChanged = !args.isInitial && args.prevValue !== args.newValue;
    try {
      callback(args);
    } finally {
      args.prevValue = args.newValue;
      args.isInitial = false;
    }
  });
  if (disposer && !args.effectDisposer) {
    args.effectDisposer = disposer;
  }
  return args.effectDisposer;
}
function derivedSignal(input, fn) {
  if (!isSignalLike(input)) {
    throw new Error("Input must be a SignalLike");
  }
  const callback = () => fn(input.value);
  if (typeof input.constructor === "function" && input.constructor !== {}.constructor) {
    try {
      let derived = new input.constructor(callback);
      let disposer2;
      if (isSignalLike(derived)) {
        if (derived.peek() === callback) {
          disposer2 = input.subscribe(() => {
            derived.value = callback();
          });
        }
        if (disposer2) {
          derived.derivedDisposer = function() {
            disposer2();
            delete derived.derivedDisposer;
          };
        }
        return derived;
      }
    } catch (error) {
    }
  }
  let primitive;
  const disposer = input.subscribe(() => {
    if (!primitive) {
      primitive = new PrimitiveComputed(callback);
      return;
    }
    primitive.update();
  });
  if (disposer) {
    primitive.derivedDisposer = function() {
      disposer();
      delete primitive.derivedDisposer;
    };
  }
  return primitive;
}
var PrimitiveComputed = class {
  constructor(fn) {
    this._subs = /* @__PURE__ */ new Set();
    this._fn = fn;
    this.update(true);
  }
  update(force) {
    const newValue = this._fn();
    if (newValue !== this._value || force) {
      this._value = newValue;
      this._subs.forEach((sub) => sub(newValue));
    }
  }
  subscribe(callback) {
    callback(this._value);
    this._subs.add(callback);
    return () => this._subs.delete(callback);
  }
  peek() {
    return this._value;
  }
  get value() {
    return this._value;
  }
};

// src/jsx-append-children.ts
function appendChild(parent, child) {
  if (parent instanceof window.HTMLTemplateElement) {
    parent.content.appendChild(child);
  } else {
    parent.appendChild(child);
  }
}
var fragmentPlaceholderIdx = 0;
var placeholderPrefix = "__domwisefrag_";
function isPlaceholder(node) {
  return node instanceof Comment && node.data.startsWith(placeholderPrefix);
}
function isFragmentWithPlaceholder(node) {
  return node instanceof DocumentFragment && isPlaceholder(node.firstChild);
}
function replaceNode(oldNode, newNode) {
  if (typeof oldNode.replaceWith === "function")
    oldNode.replaceWith(newNode);
  else
    oldNode.parentNode?.replaceChild(newNode, oldNode);
}
function wrapAsNode(value) {
  if (value instanceof DocumentFragment) {
    ++fragmentPlaceholderIdx;
    value.prepend(document.createComment(placeholderPrefix + fragmentPlaceholderIdx));
    value.append(document.createComment(placeholderPrefix + fragmentPlaceholderIdx));
    return value;
  }
  if (value instanceof Node) {
    return value;
  }
  if (!isVisibleChild(value)) {
    return document.createComment("");
  }
  return document.createTextNode(value);
}
function appendChildrenWithSignal(parent, signal2) {
  let prevNode;
  observeSignal(signal2, (args) => {
    if (args.isInitial) {
      prevNode = wrapAsNode(args.newValue);
      let prevNodeNew2 = isFragmentWithPlaceholder(prevNode) ? prevNode.firstChild : prevNode;
      appendChildren(parent, prevNode);
      args.lifecycleNode = (prevNode = prevNodeNew2) ?? parent;
      return;
    }
    if (!args.hasChanged) {
      return;
    }
    const newNode = wrapAsNode(args.newValue);
    if (isPlaceholder(prevNode)) {
      let n2;
      while (n2 = prevNode.nextSibling) {
        n2.parentNode?.removeChild(n2);
        if (n2 instanceof Comment && n2.data === prevNode.data) {
          break;
        }
      }
    }
    let prevNodeNew = isFragmentWithPlaceholder(newNode) ? newNode.firstChild : newNode;
    replaceNode(prevNode, newNode);
    args.lifecycleNode = (prevNode = prevNodeNew) ?? parent;
  });
}
function appendChildren(parent, children) {
  if (!isVisibleChild(children)) return;
  if (isArrayLike(children)) {
    for (const child of [...children]) {
      appendChildren(parent, child);
    }
  } else if (isString(children) || isNumber(children)) {
    appendChild(parent, document.createTextNode(children));
  } else if (isElement(children)) {
    appendChild(parent, children);
  } else if (isShadowRoot(children)) {
    const shadowRoot = parent.attachShadow(children.attr);
    appendChildren(shadowRoot, children.children);
    setRef(children.ref, shadowRoot);
  } else if (isSignalLike(children)) {
    appendChildrenWithSignal(parent, children);
  }
}

// src/jsx-assign-class.ts
function unsignalizePrevClass(prev) {
  if (prev == null)
    return prev;
  if (isArrayLike(prev)) {
    prev = Array.from(prev).map((item) => {
      if (isSignalLike(item))
        return item.peek();
      return item;
    });
  } else if (isObject(prev)) {
    prev = { ...prev };
    Object.entries(prev).forEach(([key, val]) => {
      if (isSignalLike(val))
        prev[key] = val.peek();
    });
  }
  return prev;
}
function clearPrevClass(node, prev) {
  if (prev == null || prev === false || prev === true)
    return;
  prev = unsignalizePrevClass(prev);
  const prevClassNames = (className(prev) ?? "").split(" ");
  for (let cls of prevClassNames) {
    if (cls) {
      node.classList.remove(cls);
    }
  }
}
function assignClass(node, value, prev) {
  if (value == null || value === false) {
    clearPrevClass(node, prev);
    return;
  }
  prev = unsignalizePrevClass(prev);
  if (isArrayLike(value)) {
    value = Array.from(value).map((x) => {
      let val = x;
      if (isSignalLike(x)) {
        observeSignal(x, (args) => {
          if (args.isInitial) {
            val = args.newValue;
            return;
          }
          applyClassName(node, args.newValue, args.prevValue);
        }, {
          lifecycleNode: node
        });
      }
      return val;
    });
  } else if (isObject(value)) {
    value = { ...value };
    Object.entries(value).forEach(([key, val]) => {
      if (isSignalLike(val)) {
        observeSignal(val, (args) => {
          if (args.isInitial) {
            value[key] = args.newValue;
            return;
          }
          applyClassName(node, Boolean(args.newValue) && key, Boolean(args.prevValue) && key);
        }, {
          lifecycleNode: node
        });
      }
    });
  }
  applyClassName(node, value, prev);
}
function applyClassName(node, value, prev) {
  const newList = (className(value) ?? "").split(" ");
  if (prev) {
    const prevList = (className(prev) ?? "").split(" ");
    for (let cls of prevList) {
      if (cls && !newList.includes(cls)) {
        node.classList.remove(cls);
      }
    }
  }
  for (let cls of newList) {
    cls && node.classList.add(cls);
  }
}

// src/jsx-assign-style.ts
function prefixKey(prefix, key) {
  return prefix + key.charAt(0).toUpperCase() + key.substring(1);
}
var isUnitlessNumber = /* @__PURE__ */ (() => {
  let rec = {
    animationIterationCount: 0,
    borderImageOutset: 0,
    borderImageSlice: 0,
    borderImageWidth: 0,
    boxFlex: 0,
    boxFlexGroup: 0,
    boxOrdinalGroup: 0,
    columnCount: 0,
    columns: 0,
    flex: 0,
    flexGrow: 0,
    flexPositive: 0,
    flexShrink: 0,
    flexNegative: 0,
    flexOrder: 0,
    gridArea: 0,
    gridRow: 0,
    gridRowEnd: 0,
    gridRowSpan: 0,
    gridRowStart: 0,
    gridColumn: 0,
    gridColumnEnd: 0,
    gridColumnSpan: 0,
    gridColumnStart: 0,
    fontWeight: 0,
    lineClamp: 0,
    lineHeight: 0,
    opacity: 0,
    order: 0,
    orphans: 0,
    tabSize: 0,
    widows: 0,
    zIndex: 0,
    zoom: 0,
    // SVG-related properties
    fillOpacity: 0,
    floodOpacity: 0,
    stopOpacity: 0,
    strokeDasharray: 0,
    strokeDashoffset: 0,
    strokeMiterlimit: 0,
    strokeOpacity: 0,
    strokeWidth: 0
  };
  const prefixes = ["Webkit", "ms", "Moz", "O"];
  Object.keys(rec).forEach((prop) => {
    prefixes.forEach((prefix) => {
      rec[prefixKey(prefix, prop)] = 0;
    });
  });
  return rec;
})();
function setStylePropValue(node, key, val) {
  if (key.indexOf("-") === 0) {
    node.style.setProperty(key, val);
  } else if (isNumber(val) && isUnitlessNumber[key] !== 0) {
    node.style[key] = val + "px";
  } else {
    node.style[key] = val;
  }
}
function clearPrevStyle(node, prev) {
  if (prev == null || prev === false || prev === true)
    return;
  if (isString(prev)) {
    node.removeAttribute("style");
    return;
  }
  if (isObject(prev)) {
    Object.keys(prev).forEach((key) => {
      setStylePropValue(node, key, "");
    });
  }
}
function assignStyle(node, value, prev) {
  if (value == null || value === false || value === true) {
    clearPrevStyle(node, prev);
    return;
  }
  if (isString(value)) {
    node.setAttribute("style", value);
    return;
  }
  if (isObject(value)) {
    if (isObject(prev)) {
      value = { ...value };
      Object.keys(prev).forEach((key) => {
        if (!(key in value))
          value[key] = "";
      });
    } else
      clearPrevStyle(node, prev);
    Object.entries(value).forEach(([key, val]) => {
      if (isSignalLike(val)) {
        observeSignal(val, (args) => setStylePropValue(node, key, args.newValue), {
          lifecycleNode: node
        });
      } else {
        setStylePropValue(node, key, val);
      }
    });
    return;
  }
}

// src/jsx-assign-props.ts
var XLinkNamespace = "http://www.w3.org/1999/xlink";
var XMLNamespace = "http://www.w3.org/XML/1998/namespace";
function normalizeAttribute(s2, separator) {
  return s2.replace(/[A-Z]/g, (match) => separator + match.toLowerCase());
}
var mappedKeys = /* @__PURE__ */ new Map([
  ["autoComplete", "autocomplete"],
  ["autoFocus", "autofocus"],
  ["className", "class"],
  ["colSpan", "colspan"],
  ["contentEditable", "contenteditable"],
  ["formNoValidate", "formnovalidate"],
  ["htmlFor", "for"],
  ["innerText", "textContent"],
  ["minLength", "minlength"],
  ["maxLength", "maxlength"],
  ["readOnly", "readonly"],
  ["reffererPolicy", "referrerpolicy"],
  ["rowSpan", "rowspan"],
  ["spellCheck", "spellcheck"],
  ["tabIndex", "tabindex"],
  ["onDoubleClick", "onDblClick"],
  ["onDoubleClickCapture", "onDblClickCapture"]
]);
function assignProp(node, key, value, prev) {
  switch (key) {
    case "dataset":
      if (prev != null) {
        Object.entries(prev).forEach(([k, v2]) => {
          if (v2 != null) {
            delete node.dataset[k];
          }
        });
      }
      Object.entries(value).forEach(([dataKey, dataValue]) => {
        if (dataValue != null) {
          node.dataset[dataKey] = dataValue;
        }
      });
      return;
    case "textContent":
      if (isVisibleChild(value)) {
        node[key] = value;
      } else if (isVisibleChild(prev)) {
        node[key] = "";
      }
      return;
    case "dangerouslySetInnerHTML":
      if (isObject(value)) {
        node.innerHTML = value["__html"];
      } else if (isObject(prev)) {
        node.innerHTML = "";
      }
      return;
    case "value":
      if (value == null) {
        if (prev != null) {
          node[key] = null;
        }
        return;
      } else if (node instanceof window.HTMLSelectElement) {
        return;
      } else if (node instanceof window.HTMLTextAreaElement) {
        node.value = value;
        return;
      }
      break;
    case "class":
      assignClass(node, value, prev);
      return;
    case "spellcheck":
      node.spellcheck = value === "" || value === true || value === "true" ? true : value === false || value === "false" ? false : value;
      return;
    case "draggable":
      if (value === false || value === "false")
        value = "false";
      else if (value === "")
        value = null;
      else if (value === true || value === "true")
        value = "true";
      break;
    case "contenteditable":
      if (value === false)
        value = "false";
      else if (value === "" || value === true)
        value = "true";
      break;
    case "ref":
    case "namespaceURI":
      return;
    case "style":
      assignStyle(node, value, prev);
      return;
    case "on":
    case "onCapture":
      const useCapture = key === "onCapture";
      if (prev != null) {
        Object.entries(prev).forEach(([eventName, eventHandler]) => {
          if (value == null || value[eventName] !== eventHandler) {
            node.removeEventListener(eventName, eventHandler, useCapture);
          }
        });
      }
      Object.entries(value).forEach(([eventName, eventHandler]) => {
        if (prev == null || prev[eventName] !== eventHandler) {
          node.addEventListener(eventName, eventHandler, useCapture);
        }
      });
      return;
    case "xlinkActuate":
    case "xlinkArcrole":
    case "xlinkHref":
    case "xlinkRole":
    case "xlinkShow":
    case "xlinkTitle":
    case "xlinkType":
      node.setAttributeNS(XLinkNamespace, normalizeAttribute(key, ":"), value);
      return;
    case "xmlnsXlink":
      node.setAttribute("xmlns:xlink", value);
      return;
    case "xmlBase":
    case "xmlLang":
    case "xmlSpace":
      node.setAttributeNS(XMLNamespace, normalizeAttribute(key, ":"), value);
      return;
  }
  if (typeof value === "function") {
    if (key[0] === "o" && key[1] === "n") {
      if (prev != null && prev === value)
        return;
      let attribute = key.toLowerCase();
      const useCapture = attribute.endsWith("capture");
      let eventName;
      if (!useCapture && (node[attribute] === null || prev != null && node[attribute] === prev)) {
        node[attribute] = value;
        addDisposingListener(node, nodeEventPropDisposer.bind(node, attribute));
      } else if (useCapture) {
        eventName = attribute.substring(2, attribute.length - 7);
        if (prev != null) {
          node.removeEventListener(eventName, prev, true);
        }
        node.addEventListener(eventName, value, true);
      } else {
        if (attribute in window) {
          const standardEventName = attribute.substring(2);
          eventName = standardEventName;
        } else {
          const customEventName = attribute[2] + key.slice(3);
          eventName = customEventName;
        }
        if (prev != null) {
          node.removeEventListener(eventName, prev);
        }
        node.addEventListener(eventName, value);
      }
    } else {
      console.warn(`A function was provided for JSX ${node.tagName} element ${key} which does not start with "on":`, value, node.tagName);
    }
    return;
  }
  if (isObject(prev)) {
    if (prev === value) {
      return;
    }
    delete node[key];
  }
  if (prev === true) {
    if (prev === value) {
      return;
    }
    node.removeAttribute(key);
  }
  if (prev !== false && prev != null) {
    if (prev === value) {
      return;
    }
    if (node instanceof SVGElement && !nonPresentationSVGAttributes.test(key)) {
      node.removeAttributeNS(normalizeAttribute(key, "-"), value);
    } else {
      node.removeAttribute(key);
    }
  }
  if (isObject(value)) {
    node[key] = value;
    return;
  }
  if (value === true) {
    node.setAttribute(key, "");
    return;
  }
  if (value !== false && value != null) {
    if (node instanceof SVGElement && !nonPresentationSVGAttributes.test(key)) {
      node.setAttribute(normalizeAttribute(key, "-"), value);
    } else {
      node.setAttribute(key, value);
    }
  }
}
function assignProps(node, props) {
  for (let [key, value] of Object.entries(props)) {
    key = mappedKeys.get(key) ?? key;
    if (isSignalLike(value)) {
      observeSignal(value, (args) => assignProp(node, key, args.newValue, args.prevValue), {
        lifecycleNode: node
      });
    } else if (isPropHook(value)) {
      value[initPropHookSymbol](node, key);
    } else {
      assignProp(node, key, value);
    }
  }
  return node;
}
function nodeEventPropDisposer(key) {
  this[key] = null;
}

// src/jsx-factory.ts
function jsx(tag, props) {
  let { children, ...attr } = props || {};
  let ns = attr.namespaceURI;
  if (!ns) {
    if (svgOnlyTags.has(tag))
      ns = SVGNamespace;
    else if (mathMLOnlyTags.has(tag))
      ns = MathMLNamespace;
    else
      ns = currentNamespaceURI();
  }
  let node;
  if (isString(tag)) {
    node = ns ? document.createElementNS(ns, tag) : document.createElement(tag);
    assignProps(node, attr);
    appendChildren(node, children);
    if (node instanceof window.HTMLSelectElement && attr.value != null) {
      if (attr.multiple === true && Array.isArray(attr.value)) {
        const values = attr.value.map((value) => String(value));
        node.querySelectorAll("option").forEach((option) => option.selected = values.includes(option.value));
      } else {
        node.value = attr.value;
      }
    }
    setRef(attr.ref, node);
  } else if (typeof tag === "function") {
    if (isObject(tag.defaultProps)) {
      attr = { ...tag.defaultProps, ...attr };
    }
    node = isComponentClass(tag) ? initComponentClass(tag, attr, children) : tag({ ...attr, children });
  } else {
    throw new TypeError(`Invalid JSX element type: ${tag}`);
  }
  return node;
}

// src/compat-api.ts
function createElement(tag, attr, ...children) {
  if (isString(attr) || Array.isArray(attr)) {
    children.unshift(attr);
    attr = {};
  }
  attr = attr || {};
  if (attr.children != null && !children.length) {
    ({ children, ...attr } = attr);
  }
  return jsx(tag, { ...attr, children });
}
function useImperativeHandle(ref, init) {
  setRef(ref, init());
}

// src/fragment.ts
function Fragment(attr) {
  const fragment = document.createDocumentFragment();
  appendChildren(fragment, attr.children);
  return fragment;
}

// src/hooks.ts
function useClassList(initialValue) {
  let temp = document.createElement("div");
  if (initialValue != null) {
    temp.className = className(initialValue);
  }
  let list = temp.classList;
  function ClassList() {
    return list;
  }
  function dispose() {
    list = null;
    temp = null;
  }
  Object.defineProperties(
    ClassList,
    Object.getOwnPropertyDescriptors({
      [initPropHookSymbol](node, prop) {
        if (prop !== "class")
          throw new Error("useClassList can only be used for 'class' attribute.");
        temp && removeDisposingListener(temp, dispose);
        list && node?.setAttribute("class", list.value);
        addDisposingListener(temp = node, dispose);
        list = node?.classList ?? list;
      },
      get size() {
        return list.length;
      },
      get value() {
        return list.value;
      },
      add(...tokens) {
        list.add(...tokens);
      },
      remove(...tokens) {
        list.remove(...tokens);
      },
      toggle(token, force) {
        list.toggle(token, force);
      },
      contains(token) {
        return list.contains(token);
      }
    })
  );
  return ClassList;
}
function propBinding(value) {
  if (arguments.length && value !== this.value) {
    this.node && this.prop && assignProp(this.node, this.prop, value, this.value);
    this.value = value;
  }
  return this.value;
}
function propBindingDispose() {
  delete this.node;
}
function propBindingInit(el, key) {
  if (this.node && el && this.node !== el || this.prop && key && this.prop !== key) {
    throw new Error("usePropBinding can only be used with one element and one attribute. Create a new setter for each element / prop.");
  }
  if (this.node)
    removeDisposingListener(this.node, this.dispose);
  this.node = el;
  if (this.node) {
    addDisposingListener(this.node, this.dispose);
    assignProp(this.node, this.prop ?? (this.prop = key), this.value);
  }
}
function usePropBinding(initialValue) {
  const accessorThis = {
    value: initialValue
  };
  accessorThis.dispose = propBindingDispose.bind(accessorThis);
  const hook = propBinding.bind(accessorThis);
  hook[initPropHookSymbol] = propBindingInit.bind(accessorThis);
  return hook;
}
function useText(initialValue) {
  const text = new Text();
  Object.defineProperty(text, "toString", {
    value() {
      return this.textContent;
    }
  });
  function setText(value) {
    text.textContent = value;
  }
  if (initialValue != null) {
    setText(initialValue);
  }
  return [text, setText];
}

// ../../node_modules/.pnpm/@preact+signals-core@1.12.1/node_modules/@preact/signals-core/dist/signals-core.module.js
var i = Symbol.for("preact-signals");
function t() {
  if (!(s > 1)) {
    var i2, t2 = false;
    while (void 0 !== h) {
      var r2 = h;
      h = void 0;
      f++;
      while (void 0 !== r2) {
        var o2 = r2.o;
        r2.o = void 0;
        r2.f &= -3;
        if (!(8 & r2.f) && c(r2)) try {
          r2.c();
        } catch (r3) {
          if (!t2) {
            i2 = r3;
            t2 = true;
          }
        }
        r2 = o2;
      }
    }
    f = 0;
    s--;
    if (t2) throw i2;
  } else s--;
}
function r(i2) {
  if (s > 0) return i2();
  s++;
  try {
    return i2();
  } finally {
    t();
  }
}
var o = void 0;
function n(i2) {
  var t2 = o;
  o = void 0;
  try {
    return i2();
  } finally {
    o = t2;
  }
}
var h = void 0;
var s = 0;
var f = 0;
var v = 0;
function e(i2) {
  if (void 0 !== o) {
    var t2 = i2.n;
    if (void 0 === t2 || t2.t !== o) {
      t2 = { i: 0, S: i2, p: o.s, n: void 0, t: o, e: void 0, x: void 0, r: t2 };
      if (void 0 !== o.s) o.s.n = t2;
      o.s = t2;
      i2.n = t2;
      if (32 & o.f) i2.S(t2);
      return t2;
    } else if (-1 === t2.i) {
      t2.i = 0;
      if (void 0 !== t2.n) {
        t2.n.p = t2.p;
        if (void 0 !== t2.p) t2.p.n = t2.n;
        t2.p = o.s;
        t2.n = void 0;
        o.s.n = t2;
        o.s = t2;
      }
      return t2;
    }
  }
}
function u(i2, t2) {
  this.v = i2;
  this.i = 0;
  this.n = void 0;
  this.t = void 0;
  this.W = null == t2 ? void 0 : t2.watched;
  this.Z = null == t2 ? void 0 : t2.unwatched;
  this.name = null == t2 ? void 0 : t2.name;
}
u.prototype.brand = i;
u.prototype.h = function() {
  return true;
};
u.prototype.S = function(i2) {
  var t2 = this, r2 = this.t;
  if (r2 !== i2 && void 0 === i2.e) {
    i2.x = r2;
    this.t = i2;
    if (void 0 !== r2) r2.e = i2;
    else n(function() {
      var i3;
      null == (i3 = t2.W) || i3.call(t2);
    });
  }
};
u.prototype.U = function(i2) {
  var t2 = this;
  if (void 0 !== this.t) {
    var r2 = i2.e, o2 = i2.x;
    if (void 0 !== r2) {
      r2.x = o2;
      i2.e = void 0;
    }
    if (void 0 !== o2) {
      o2.e = r2;
      i2.x = void 0;
    }
    if (i2 === this.t) {
      this.t = o2;
      if (void 0 === o2) n(function() {
        var i3;
        null == (i3 = t2.Z) || i3.call(t2);
      });
    }
  }
};
u.prototype.subscribe = function(i2) {
  var t2 = this;
  return E(function() {
    var r2 = t2.value, n2 = o;
    o = void 0;
    try {
      i2(r2);
    } finally {
      o = n2;
    }
  }, { name: "sub" });
};
u.prototype.valueOf = function() {
  return this.value;
};
u.prototype.toString = function() {
  return this.value + "";
};
u.prototype.toJSON = function() {
  return this.value;
};
u.prototype.peek = function() {
  var i2 = o;
  o = void 0;
  try {
    return this.value;
  } finally {
    o = i2;
  }
};
Object.defineProperty(u.prototype, "value", { get: function() {
  var i2 = e(this);
  if (void 0 !== i2) i2.i = this.i;
  return this.v;
}, set: function(i2) {
  if (i2 !== this.v) {
    if (f > 100) throw new Error("Cycle detected");
    this.v = i2;
    this.i++;
    v++;
    s++;
    try {
      for (var r2 = this.t; void 0 !== r2; r2 = r2.x) r2.t.N();
    } finally {
      t();
    }
  }
} });
function d(i2, t2) {
  return new u(i2, t2);
}
function c(i2) {
  for (var t2 = i2.s; void 0 !== t2; t2 = t2.n) if (t2.S.i !== t2.i || !t2.S.h() || t2.S.i !== t2.i) return true;
  return false;
}
function a(i2) {
  for (var t2 = i2.s; void 0 !== t2; t2 = t2.n) {
    var r2 = t2.S.n;
    if (void 0 !== r2) t2.r = r2;
    t2.S.n = t2;
    t2.i = -1;
    if (void 0 === t2.n) {
      i2.s = t2;
      break;
    }
  }
}
function l(i2) {
  var t2 = i2.s, r2 = void 0;
  while (void 0 !== t2) {
    var o2 = t2.p;
    if (-1 === t2.i) {
      t2.S.U(t2);
      if (void 0 !== o2) o2.n = t2.n;
      if (void 0 !== t2.n) t2.n.p = o2;
    } else r2 = t2;
    t2.S.n = t2.r;
    if (void 0 !== t2.r) t2.r = void 0;
    t2 = o2;
  }
  i2.s = r2;
}
function y(i2, t2) {
  u.call(this, void 0);
  this.x = i2;
  this.s = void 0;
  this.g = v - 1;
  this.f = 4;
  this.W = null == t2 ? void 0 : t2.watched;
  this.Z = null == t2 ? void 0 : t2.unwatched;
  this.name = null == t2 ? void 0 : t2.name;
}
y.prototype = new u();
y.prototype.h = function() {
  this.f &= -3;
  if (1 & this.f) return false;
  if (32 == (36 & this.f)) return true;
  this.f &= -5;
  if (this.g === v) return true;
  this.g = v;
  this.f |= 1;
  if (this.i > 0 && !c(this)) {
    this.f &= -2;
    return true;
  }
  var i2 = o;
  try {
    a(this);
    o = this;
    var t2 = this.x();
    if (16 & this.f || this.v !== t2 || 0 === this.i) {
      this.v = t2;
      this.f &= -17;
      this.i++;
    }
  } catch (i3) {
    this.v = i3;
    this.f |= 16;
    this.i++;
  }
  o = i2;
  l(this);
  this.f &= -2;
  return true;
};
y.prototype.S = function(i2) {
  if (void 0 === this.t) {
    this.f |= 36;
    for (var t2 = this.s; void 0 !== t2; t2 = t2.n) t2.S.S(t2);
  }
  u.prototype.S.call(this, i2);
};
y.prototype.U = function(i2) {
  if (void 0 !== this.t) {
    u.prototype.U.call(this, i2);
    if (void 0 === this.t) {
      this.f &= -33;
      for (var t2 = this.s; void 0 !== t2; t2 = t2.n) t2.S.U(t2);
    }
  }
};
y.prototype.N = function() {
  if (!(2 & this.f)) {
    this.f |= 6;
    for (var i2 = this.t; void 0 !== i2; i2 = i2.x) i2.t.N();
  }
};
Object.defineProperty(y.prototype, "value", { get: function() {
  if (1 & this.f) throw new Error("Cycle detected");
  var i2 = e(this);
  this.h();
  if (void 0 !== i2) i2.i = this.i;
  if (16 & this.f) throw this.v;
  return this.v;
} });
function w(i2, t2) {
  return new y(i2, t2);
}
function _(i2) {
  var r2 = i2.u;
  i2.u = void 0;
  if ("function" == typeof r2) {
    s++;
    var n2 = o;
    o = void 0;
    try {
      r2();
    } catch (t2) {
      i2.f &= -2;
      i2.f |= 8;
      b(i2);
      throw t2;
    } finally {
      o = n2;
      t();
    }
  }
}
function b(i2) {
  for (var t2 = i2.s; void 0 !== t2; t2 = t2.n) t2.S.U(t2);
  i2.x = void 0;
  i2.s = void 0;
  _(i2);
}
function g(i2) {
  if (o !== this) throw new Error("Out-of-order effect");
  l(this);
  o = i2;
  this.f &= -2;
  if (8 & this.f) b(this);
  t();
}
function p(i2, t2) {
  this.x = i2;
  this.u = void 0;
  this.s = void 0;
  this.o = void 0;
  this.f = 32;
  this.name = null == t2 ? void 0 : t2.name;
}
p.prototype.c = function() {
  var i2 = this.S();
  try {
    if (8 & this.f) return;
    if (void 0 === this.x) return;
    var t2 = this.x();
    if ("function" == typeof t2) this.u = t2;
  } finally {
    i2();
  }
};
p.prototype.S = function() {
  if (1 & this.f) throw new Error("Cycle detected");
  this.f |= 1;
  this.f &= -9;
  _(this);
  a(this);
  s++;
  var i2 = o;
  o = this;
  return g.bind(this, i2);
};
p.prototype.N = function() {
  if (!(2 & this.f)) {
    this.f |= 2;
    this.o = h;
    h = this;
  }
};
p.prototype.d = function() {
  this.f |= 8;
  if (!(1 & this.f)) b(this);
};
p.prototype.dispose = function() {
  this.d();
};
function E(i2, t2) {
  var r2 = new p(i2, t2);
  try {
    r2.c();
  } catch (i3) {
    r2.d();
    throw i3;
  }
  var o2 = r2.d.bind(r2);
  o2[Symbol.dispose] = o2;
  return o2;
}

// src/signals.ts
var signal = d;
var computed = w;
var effect = E;
var batch = r;
var untracked = n;
function useSignal(initialValue) {
  return signal(initialValue);
}
export {
  Component,
  Fragment,
  MathMLNamespace,
  SVGNamespace,
  ShadowRootNode,
  addDisposingListener,
  batch,
  bindThis,
  className,
  computed,
  createElement,
  createRef,
  currentLifecycleRoot,
  currentNamespaceURI,
  derivedSignal,
  dispatchDisposingEvent,
  effect,
  createElement as h,
  inHTMLNamespace,
  inMathMLNamespace,
  inNamespaceURI,
  inSVGNamespace,
  initPropHookSymbol,
  invokeDisposingListeners,
  isSignalLike,
  jsx,
  jsx as jsxs,
  observeSignal,
  removeDisposingListener,
  setRef,
  signal,
  untracked,
  useClassList,
  useImperativeHandle,
  usePropBinding,
  createRef as useRef,
  useSignal,
  useText
};
